import React, { useEffect, useState, useMemo } from "react";
import api from "../api/api";
import { Trophy, Award, Filter, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const RATING_META: Record<string, { color: string; bg: string; label: string; emoji: string }> = {
  excellent:         { color: "text-green-700",  bg: "bg-green-50 border-green-200",   label: "Excellent",          emoji: "🏆" },
  very_good:         { color: "text-blue-700",   bg: "bg-blue-50 border-blue-200",     label: "Very Good",          emoji: "⭐" },
  good:              { color: "text-teal-700",   bg: "bg-teal-50 border-teal-200",     label: "Good",               emoji: "🟢" },
  needs_improvement: { color: "text-amber-700",  bg: "bg-amber-50 border-amber-200",   label: "Needs Improvement",  emoji: "🟡" },
  poor:              { color: "text-red-700",    bg: "bg-red-50 border-red-200",       label: "Poor",               emoji: "🔴" },
};

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function EmployeePoints() {
  const { user, isAdmin } = useAuth();
  const isManagerOrAdmin = isAdmin || user?.role === "MANAGER" || user?.role === "DEPARTMENT_HEAD";
  const [month, setMonth] = useState<string>(currentMonth());
  const [data, setData] = useState<any>({ employees: [], totalTickets: 0 });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  // Filters
  const [employeeFilter, setEmployeeFilter] = useState<string>("");
  const [deptFilter, setDeptFilter] = useState<string>("");
  const [ratingFilter, setRatingFilter] = useState<string>("");
  const [minPoints, setMinPoints] = useState<string>("");

  const load = () => {
    setLoading(true);
    api.get("/whmcs/employee-points", { params: { month } })
      .then(r => setData(r.data || { employees: [], totalTickets: 0 }))
      .catch(() => setData({ employees: [], totalTickets: 0 }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const allEmployees = data.employees || [];

  // Derived lists for filter dropdowns
  const distinctEmployees = useMemo(
    () => allEmployees.map((e: any) => ({ id: e.employeeId, name: e.name })),
    [allEmployees]
  );
  const distinctDepts = useMemo(
    () => Array.from(new Set(allEmployees.map((e: any) => e.department).filter(Boolean))).sort(),
    [allEmployees]
  );

  // Apply filters (client-side)
  const employees = useMemo(() => {
    let r = allEmployees;
    if (employeeFilter) r = r.filter((e: any) => e.employeeId === employeeFilter);
    if (deptFilter)     r = r.filter((e: any) => e.department === deptFilter);
    if (ratingFilter)   r = r.filter((e: any) => e.rating === ratingFilter);
    if (minPoints)      r = r.filter((e: any) => (e.totalPoints || 0) >= Number(minPoints));
    return r;
  }, [allEmployees, employeeFilter, deptFilter, ratingFilter, minPoints]);

  const grandTotal = employees.reduce((s: number, e: any) => s + (e.totalPoints || 0), 0);
  const hasFilters = employeeFilter || deptFilter || ratingFilter || minPoints;

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Trophy className="text-amber-500" size={28}/>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Employee Points</h1>
            <p className="text-sm text-gray-500">
              Monthly performance based on WHMCS ticket contributions
            </p>
          </div>
        </div>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"/>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
          <Filter size={13}/> Filters
          {hasFilters && (
            <button onClick={() => { setEmployeeFilter(""); setDeptFilter(""); setRatingFilter(""); setMinPoints(""); }}
              className="ml-auto flex items-center gap-1 text-red-500 hover:text-red-700 font-normal">
              <X size={11}/> Reset
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          {isManagerOrAdmin ? (
            <select value={employeeFilter} onChange={e => setEmployeeFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">All Employees</option>
              {distinctEmployees.map((e: any) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          ) : (
            <div className="text-xs text-gray-400 italic px-3 py-2">Showing your points only</div>
          )}
          {isManagerOrAdmin && (
            <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">All Departments</option>
              {distinctDepts.map((d: any) => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          <select value={ratingFilter} onChange={e => setRatingFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">All Ratings</option>
            <option value="excellent">🏆 Excellent</option>
            <option value="very_good">⭐ Very Good</option>
            <option value="good">🟢 Good</option>
            <option value="needs_improvement">🟡 Needs Improvement</option>
            <option value="poor">🔴 Poor</option>
          </select>
          <input type="number" placeholder="Min Points (e.g. 50)" value={minPoints} onChange={e => setMinPoints(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"/>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-xs text-gray-500">Employees</div>
          <div className="text-2xl font-bold text-gray-800">{employees.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-xs text-gray-500">Tickets in Month</div>
          <div className="text-2xl font-bold text-gray-800">{data.totalTickets || 0}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-xs text-gray-500">Total Points</div>
          <div className="text-2xl font-bold text-gray-800">{grandTotal.toFixed(1)}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-xs text-gray-500">Avg per Employee</div>
          <div className="text-2xl font-bold text-gray-800">{employees.length ? (grandTotal / employees.length).toFixed(1) : "—"}</div>
        </div>
      </div>

      {/* Rating thresholds legend (Manager only) */}
      {isManagerOrAdmin && (
        <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 text-xs">
          <div className="font-semibold text-gray-600 mb-1">Rating scale:</div>
          <div className="flex flex-wrap gap-2">
            <span className="px-2 py-1 rounded bg-green-50 text-green-700 border border-green-200">🏆 Excellent: 120+</span>
            <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">⭐ Very Good: 100-119</span>
            <span className="px-2 py-1 rounded bg-teal-50 text-teal-700 border border-teal-200">🟢 Good: 80-99</span>
            <span className="px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200">🟡 Needs Improvement: 60-79</span>
            <span className="px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200">🔴 Poor: &lt; 60</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-500 py-8">Loading…</div>
      ) : employees.length === 0 ? (
        <div className="text-center text-gray-400 py-16 bg-white rounded-xl border border-gray-200">
          No points earned this month yet.
          <div className="text-xs mt-2">Points are distributed when tickets are classified and admins have substantial replies (&gt;20 words).</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total Points</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Rating</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-purple-600 uppercase" title="WHMCS Ticket points">🎧 WHMCS</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-blue-600 uppercase" title="Standalone Task points">✅ Tasks</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-green-600 uppercase" title="Project Step points">🗂️ Steps</th>
                {isManagerOrAdmin && (
                  <>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Simple</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Normal</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Critical</th>
                  </>
                )}
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {employees.map((e: any, idx: number) => {
                const rating = RATING_META[e.rating] || RATING_META.poor;
                const isMe = user?.id === e.employeeId;
                return (
                  <React.Fragment key={e.employeeId}>
                    <tr className={`hover:bg-gray-50 ${isMe ? "bg-indigo-50/30" : ""}`}>
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs">#{idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-bold">
                            {(e.name || "?")[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-gray-800">{e.name}</div>
                            {e.department && <div className="text-xs text-gray-400">{e.department}</div>}
                          </div>
                          {isMe && <span className="text-[10px] text-indigo-500 font-semibold">(you)</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-2xl font-bold text-gray-800">{e.totalPoints?.toFixed(1)}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${rating.bg} ${rating.color} border`}>
                          <span>{rating.emoji}</span> {rating.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-xs">
                        <span className={`px-2 py-0.5 rounded ${(e.whmcsPoints || 0) > 0 ? "bg-purple-50 text-purple-700 font-semibold" : "text-gray-300"}`}>
                          {(e.whmcsPoints || 0).toFixed(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-xs">
                        <span className={`px-2 py-0.5 rounded ${(e.taskPoints || 0) > 0 ? "bg-blue-50 text-blue-700 font-semibold" : "text-gray-300"}`}>
                          {(e.taskPoints || 0).toFixed(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-xs">
                        <span className={`px-2 py-0.5 rounded ${(e.stepPoints || 0) > 0 ? "bg-green-50 text-green-700 font-semibold" : "text-gray-300"}`}>
                          {(e.stepPoints || 0).toFixed(1)}
                        </span>
                      </td>
                      {isManagerOrAdmin && (
                        <>
                          <td className="px-4 py-3 text-center text-xs text-gray-600">{e.simpleCount || 0}</td>
                          <td className="px-4 py-3 text-center text-xs text-gray-600">{e.normalCount || 0}</td>
                          <td className="px-4 py-3 text-center text-xs text-gray-600">{e.criticalCount || 0}</td>
                        </>
                      )}
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => setExpanded(expanded === e.employeeId ? null : e.employeeId)}
                          className="text-xs text-indigo-600 hover:underline">
                          {expanded === e.employeeId ? "Hide" : "View"} ({(e.tickets || []).length})
                        </button>
                      </td>
                    </tr>
                    {expanded === e.employeeId && (
                      <tr className="bg-gray-50">
                        <td colSpan={isManagerOrAdmin ? 11 : 8} className="px-4 py-3">
                          <div className="space-y-1 text-xs">
                            {(e.tickets || []).map((t: any, i: number) => (
                              <div key={i} className="flex items-center gap-2 py-1 border-b border-gray-100 last:border-0">
                                <span className="font-mono text-gray-500 w-16">#{t.tid}</span>
                                <span className="flex-1 truncate text-gray-700">{t.subject}</span>
                                <span className="text-gray-600">{t.type}</span>
                                {isManagerOrAdmin && (
                                  <span className="text-gray-400">·  {t.points}pt total</span>
                                )}
                                <span className="text-indigo-700 font-semibold w-12 text-right">+{t.share.toFixed(1)}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
